import { ISessionManagerProvider } from 'interfaces/providers/ISessionManagerProvider';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import type { IStrikeZoneDecisionModel } from 'interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { IPlayer } from 'interfaces/team/IPlayer';
import { action, computed, IObservableArray, makeObservable, observable, runInAction } from 'mobx';
import { Lineup } from 'models/tagging/Lineup';
import moment from 'moment';
import { SessionManagerService } from 'services/data-services/SessionManagerService';
import { HotkeyService } from 'services/helper-services/HotkeyService';
import { apiPlayModelToBpPlayMap, bpPlayToApiPlayModelMap } from 'services/helper-services/mappingHelpers/bpMappings';
import { updatePlayWithStrikeZoneDecision } from 'services/helper-services/playManipulationHelpers';
import { ContextStore } from 'stores/ContextStore';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { Ruleset } from '../../enums/Ruleset';
import { IApiBallFinal } from '../../interfaces/broadcast/IApiBallFinal';
import { ITaggingProvider } from '../../interfaces/providers/ITaggingProvider';
import { LineupManager } from '../../models/setLineup/LineupManager';
import { BpPlay } from '../../models/tagging/BpPlay';
import { LogService } from '../../services/data-services/LogService';

export class BpTaggingStore {
  private taggingService: ITaggingProvider;

  private sessionStore: SessionStore;

  private lineupStore: LineupStore;

  private sessionService: ISessionManagerProvider;

  private hotkeyService: HotkeyService;

  private contextStore: ContextStore;

  private notificationStore: NotificationStore;

  constructor(
    taggingService: ITaggingProvider,
    lineupStore: LineupStore,
    sessionStore: SessionStore,
    sessionService: SessionManagerService,
    logService: LogService,
    hotkeyService: HotkeyService,
    contextStore: ContextStore,
    notificationStore: NotificationStore
  ) {
    this.taggingService = taggingService;
    this.sessionStore = sessionStore;
    this.logService = logService;
    this.lineupStore = lineupStore;
    this.sessionService = sessionService;
    this.hotkeyService = hotkeyService;
    this.contextStore = contextStore;
    this.placeholderPlay = new BpPlay();
    this.notificationStore = notificationStore;

    makeObservable(this);
  }

  @observable isLoading = false;

  @observable isTaggingHits = false;

  logService: LogService;

  @observable teamRoster: IPlayer[] = [];

  @observable playsReversed: BpPlay[] = [];

  @observable selectedPlay?: BpPlay;

  @observable placeholderPlay: BpPlay;

  @observable isInitialized = false;

  @observable isTagging = false;

  isSaving = false;

  @observable lastStrikeZoneDecision?: IStrikeZoneDecisionModel;

  @computed get playCount() {
    return this.playsReversed.filter((p) => p.isLive).length;
  }

  @computed get livePlaysReversed() {
    return this.playsReversed.filter((p) => p.isLive);
  }

  @computed get isLivePlaySelected() {
    return this.selectedPlay && this.selectedPlay.id !== this.placeholderPlay.id;
  }

  @computed get displayPlays() {
    return [this.placeholderPlay, ...this.livePlaysReversed];
  }

  @computed get shouldDisplayPlaceholder() {
    const play = this.livePlaysReversed.length > 0 ? this.livePlaysReversed[0] : undefined;
    return this.sessionStore.isPlaying && !this.isTaggingHits && (!play || play.isValidated);
  }

  @action resumeSession = async () => {
    const mode = this.sessionStore.currentSessionMode;
    if (!this.sessionStore.isGameInProgress || !this.sessionStore.isMetadataLineupSet) {
      this.logService.logError(
        {
          message: 'Cannot resume tagging',
          sessionMetadata: this.sessionStore.currentSessionMetadata,
          sessionMode: this.sessionStore.currentSessionMode,
        },
        {
          function: 'BpTaggingStore@resumeSession',
        }
      );
      this.notificationStore.addNotification({
        id: 'cannot-resume-tagging',
        message: 'Cannot resume tagging. Session is in incorrect state or lineup has not been set',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('cannot-resume-tagging');
        },
      });
      return;
    }

    let plays;

    try {
      plays = await this.taggingService.getPlaysWithBallFinals(mode.sessionId!);
      await this.getRosterFromLocalDB(mode.sessionId!);
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'error-fetching-plays',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('error-fetching-plays');
          },
        });
      }
    }

    if (plays) {
      plays.plays.sort((a, b) => b.play.taggerBehavior.sequenceNumber - a.play.taggerBehavior.sequenceNumber);
      this.playsReversed = plays.plays.map((p) => apiPlayModelToBpPlayMap(p.play, p.ballFinals));
    }
    this.recreatePlaceholderPlay();
    this.isInitialized = true;
    return this.isInitialized;
  };

  @action initializeFromLineup = async (lineupManager: LineupManager, owningOrganizationId?: string) => {
    this.isLoading = true;
    const sessionMode = this.sessionStore.currentSessionMode;
    const sessionMetadata = {
      sessionId: sessionMode.sessionId || '',
      owningOrganizationId,
      homeTeam: lineupManager.getMetadataTeam(),
      awayTeam: null,
      league: undefined,
      level: undefined,
      ruleSet: Ruleset.battingPractice,
      location: {},
      tagger: { id: null },
      sessionTaggingState: {
        state: 'InitialLineupSet',
        sessionStartedUtc: sessionMode.time,
        sessionStartedLocal: '',
        playCountAfterApprovalplayCount: 0,
        initialLineupSetUtc: moment().toISOString(),
        initialLineupSetLocal: new Date(),
      },
      time: moment().toISOString(),
      sessionModeState: {
        state: sessionMode.sessionMode,
        sessionStartedUtc: sessionMode.time,
        sessionStartedLocal: '',
      },
      sessionType: sessionMode.sessionType,
      privateSession: sessionMode.privateSession,
      systemId: this.contextStore.activeField!.systemId,
      isVideoCaptureEnabled: sessionMode.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: sessionMode.isPlayerTrackingEnabled,
      strikeZone: this.sessionStore.currentSessionMetadata.strikeZone,
      isOperatorlessTaggingEnabled: this.sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled,
    } as IApiSessionMetadataUpdated;
    await this.sessionStore.updateSessionMetadata(sessionMetadata, false);
    this.recreatePlaceholderPlay();
    await this.submitRostersToLocalDB(sessionMode.sessionId, lineupManager.roster, []);
    await this.getRosterFromLocalDB(sessionMode.sessionId!);
    this.playsReversed = [];
    this.selectedPlay = undefined;
    this.isInitialized = true;
    this.isLoading = false;
  };

  @action recreatePlaceholderPlay = () => {
    const play = this.createNextPlay();
    play.isPlaceholder = true;
    if (this.selectedPlay === this.placeholderPlay) {
      this.selectedPlay = play;
    }
    this.placeholderPlay = play;
  };

  @action submitRostersToLocalDB = async (sessionId: string | undefined, homeTeamRoster: IPlayer[], awayTeamRoster: IPlayer[]) => {
    if (sessionId) {
      return this.sessionService.submitSessionRoster({
        sessionId,
        homeTeamRoster,
        awayTeamRoster,
      });
    }
  };

  @action getRosterFromLocalDB = async (sessionId: string) => {
    const sessionRosters = await this.sessionService.getSessionRoster(sessionId);
    if (!sessionRosters) {
      throw Error("Couldn't fetch rosters");
    }
    this.teamRoster = sessionRosters.homeTeamRoster;
  };

  @action disableEnableHotkeys = (disabled: boolean) => {
    if (disabled) {
      this.hotkeyService.pause();
    } else {
      this.hotkeyService.resume();
    }
  };

  @action setIsTagging(isTagging: boolean) {
    this.isTagging = isTagging;
  }

  @action createOrUpdatePlayFromBroadcast = async (ballFinal: IApiBallFinal) => {
    let play = this.playsReversed.find((p) => p.playId === ballFinal.playId);
    if (!play) {
      play = this.createNextPlay(ballFinal);
      this.playsReversed.splice(0, 0, play);
      this.lastStrikeZoneDecision &&
        this.lastStrikeZoneDecision.playId === play.playId &&
        updatePlayWithStrikeZoneDecision(play, this.lastStrikeZoneDecision);
    } else {
      play.ballFinals.push(ballFinal);
    }
    if (play.isLive && (!this.selectedPlay || this.selectedPlay.isPlaceholder)) {
      this.selectedPlay = play;
    }
    try {
      this.isSaving = true;
      await this.taggingService.storePlay(
        bpPlayToApiPlayModelMap(play, this.sessionStore.currentSessionMetadata),
        'BpTaggingStore@createOrUpdatePlayFromBroadcast'
      );
    } finally {
      this.isSaving = false;
    }
  };

  @action updateLineup = async (play: BpPlay, newLineup: ApiLineupDraggablePlayer[]) => {
    // Update batting order
    newLineup.forEach((entry, index) => {
      entry.battingOrder = index;
    });

    (play.playLineup.players as IObservableArray<ApiLineupDraggablePlayer>).replace(newLineup);
    const metadata = { ...this.sessionStore.currentSessionMetadata };
    if (metadata.homeTeam) {
      metadata.homeTeam = { ...metadata.homeTeam, lineup: newLineup };
    }
    await this.sessionStore.updateSessionMetadata(metadata, false);
  };

  @action updateLineupOrder = async (play: BpPlay, newLineup: ApiLineupDraggablePlayer[]) => {
    await this.updateLineup(play, newLineup);
  };

  @action storeTag = async () => {
    if (!this.isLivePlaySelected) {
      return;
    }
    const play = this.selectedPlay!;
    this.taggingService.storePlay(bpPlayToApiPlayModelMap(play, this.sessionStore.currentSessionMetadata), 'BpTaggingStore@storeTag');
    play.isTagStored = true;
    play.isValidated = true;
    play.isChanged = false;
    play.shouldAnimate = true;
    this.recreatePlaceholderPlay();
    if (this.shouldDisplayPlaceholder && play.id === this.livePlaysReversed[0].id) {
      this.selectedPlay = this.placeholderPlay;
    }
  };

  private createNextPlay = (ballFinal?: IApiBallFinal) => {
    const play = new BpPlay(ballFinal);
    play.isWarmup = this.sessionStore.isWarmup;
    play.playLineup = new Lineup(this.sessionStore.currentSessionMetadata.homeTeam?.lineup ?? []);
    play.sequenceNumber = Math.max(0, ...this.playsReversed.map((p) => p.sequenceNumber)) + 1;
    play.playSequenceNumber = this.sessionStore.isWarmup
      ? undefined
      : Math.max(0, ...this.playsReversed.filter((p) => p.playSequenceNumber !== undefined).map((p) => p.sequenceNumber)) + 1;
    play.isTagStored = true;

    const lastLivePlay = this.playsReversed.find((p) => p.isLive);
    if (lastLivePlay && play.playLineup.isPlayerInLineup(lastLivePlay.batter.id)) {
      play.batter = { ...lastLivePlay.batter };
    } else {
      play.batter = { ...play.playLineup.getNextBatter(undefined) };
    }
    return play;
  };

  @action handleStrikeZoneDecision = async (decision: IStrikeZoneDecisionModel) => {
    this.lastStrikeZoneDecision = decision;
    const existingPlay = this.displayPlays.find((play) => play.playId === decision.playId);
    if (existingPlay) {
      runInAction(() => updatePlayWithStrikeZoneDecision(existingPlay!, decision));
    }
  };
}
